// PanelConfigModal.jsx - REFINED MINIMAL PREMIUM DESIGN
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  ScatterChart, Scatter, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  X as LucideX, Database, Eye, Save, RefreshCw, AlertCircle, Plus, Trash2, Settings, Globe,
  AlignLeft, RotateCw, Hash, Type, Tag, ChevronDown, Edit2
} from 'lucide-react';

import { COLORS, VIZ_TYPES, DEFAULT_PANEL_CONFIG } from './constants';
import questdbService from '../../services/questdb';
import mysqlService, { mysqlErrorCode, NO_INSTANCE_CODE } from '../../services/mysql';

import SimpleTransformations from './simpleTransformations';
import TransformationPanel from './TransformationPanel';

const X = LucideX;

// Timezone options
const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'local', label: 'Local Browser Time' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

// Number format presets
const NUMBER_FORMATS = [
  { value: 'none', label: 'None', example: '1234.567' },
  { value: 'number', label: 'Number', example: '1,234.57' },
  { value: 'percent', label: 'Percent (0-100)', example: '12.35%' },
  { value: 'percent_decimal', label: 'Percent (0-1)', example: '0.12%' },
  { value: 'scientific', label: 'Scientific', example: '1.23e+3' },
  { value: 'bytes', label: 'Bytes', example: '1.21 KB' },
  { value: 'bits', label: 'Bits', example: '9.87 Kbit' },
  { value: 'bps', label: 'Bits/sec', example: '9.87 Kbps' },
  { value: 'Bps', label: 'Bytes/sec', example: '1.21 KB/s' },
  { value: 'duration_ms', label: 'Duration (ms)', example: '1m 23s' },
  { value: 'duration_s', label: 'Duration (s)', example: '20m 34s' },
  { value: 'currency_usd', label: 'Currency (USD)', example: '$1,234.57' },
  { value: 'currency_eur', label: 'Currency (EUR)', example: '€1,234.57' },
  { value: 'custom', label: 'Custom Format', example: 'Custom' }
];

// Axis label rotation options
const LABEL_ROTATIONS = [
  { value: 0, label: 'Horizontal (0°)' },
  { value: -45, label: 'Diagonal (-45°)' },
  { value: -90, label: 'Vertical (-90°)' },
  { value: 45, label: 'Diagonal (45°)' },
  { value: 90, label: 'Vertical (90°)' }
];

// ✅ FIX 2: Legend position options
const LEGEND_POSITIONS = [
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' }
];

// ✅ FIX 1: Move Input/Select OUTSIDE component to prevent recreation
const Input = React.memo(({ label, value, onChange, placeholder, type = "text", theme, ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <label style={{ 
        display: 'block', 
        marginBottom: '6px', 
        fontSize: '11px', 
        fontWeight: '500',
        color: theme.textMuted,
        letterSpacing: '0.02em'
      }}>
        {label}
      </label>
    )}
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '8px 10px',
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: '6px',
        color: theme.text,
        fontSize: '13px',
        transition: 'all 0.15s ease',
        outline: 'none',
        ...props.style
      }}
      onFocus={(e) => e.target.style.borderColor = theme.accent}
      onBlur={(e) => e.target.style.borderColor = theme.border}
      {...props}
    />
  </div>
));

const Select = React.memo(({ label, value, onChange, children, theme, ...props }) => (
  <div style={{ marginBottom: '12px' }}>
    {label && (
      <label style={{ 
        display: 'block', 
        marginBottom: '6px', 
        fontSize: '11px', 
        fontWeight: '500',
        color: theme.textMuted,
        letterSpacing: '0.02em'
      }}>
        {label}
      </label>
    )}
    <select
      value={value}
      onChange={onChange}
      style={{
        width: '100%',
        padding: '8px 36px 8px 10px',
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: '6px',
        color: theme.text,
        fontSize: '13px',
        transition: 'all 0.15s ease',
        outline: 'none',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
        ...props.style
      }}
      onFocus={(e) => e.target.style.borderColor = theme.accent}
      onBlur={(e) => e.target.style.borderColor = theme.border}
      {...props}
    >
      {children}
    </select>
  </div>
));

const VizTypeDropdown = React.memo(({ value, onChange, theme }) => {
  const [open, setOpen] = React.useState(false);
  const selected = VIZ_TYPES.find(t => t.id === value) || VIZ_TYPES[0];
  const Icon = selected.icon;
  return (
    <div style={{ marginBottom: 12, position: 'relative' }}>
      <label style={{ display: 'block', marginBottom: 6, fontSize: 11, fontWeight: 500, color: theme.textMuted, letterSpacing: '0.02em' }}>
        Visualization Type
      </label>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 10px',
          background: theme.bg, border: `1px solid ${open ? theme.accent : theme.border}`,
          borderRadius: 6, color: theme.text, fontSize: 13, cursor: 'pointer', outline: 'none'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={14} color={theme.accent} />
          {selected.name}
        </span>
        <ChevronDown size={14} color={theme.textMuted} style={{ flexShrink: 0, marginRight: 4 }} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 6,
          marginTop: 2, overflow: 'hidden', boxShadow: theme.shadowMd
        }}>
          {VIZ_TYPES.map(type => {
            const TIcon = type.icon;
            const isActive = type.id === value;
            return (
              <button
                key={type.id}
                onClick={() => { onChange(type.id); setOpen(false); }}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  background: isActive ? 'rgba(99,102,241,0.12)' : 'transparent',
                  border: 'none', outline: 'none',
                  color: isActive ? theme.accent : theme.text,
                  fontSize: 13, cursor: 'pointer', textAlign: 'left'
                }}
                onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = theme.cardHover)}
                onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'transparent')}
              >
                <TIcon size={14} color={isActive ? theme.accent : theme.textMuted} />
                {type.name}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

function PanelConfigModal({ panel, onSave, onClose, allTables, darkMode, plantId,
  dashboards = [], currentDashboardId = null,
  dashboardVariables = [], variableValues = {} }) {
  const [config, setConfig] = useState(() => {
    if (panel) {
      return {
        ...DEFAULT_PANEL_CONFIG,
        ...panel,
        timezone: panel.timezone || 'UTC',
        yAxisScale: panel.yAxisScale || 'auto',
        yAxisMin: panel.yAxisMin || '',
        yAxisMax: panel.yAxisMax || '',
        xAxisScale: panel.xAxisScale || 'auto',
        // X-axis mode toggle. 'timestamp' = current behavior (time-series with
        // time-range filtering). 'simple' = X-axis is any column, no time-range.
        // Defaults to 'timestamp' so existing panels render exactly as before.
        xAxisMode: panel.xAxisMode || 'timestamp',
        xAxisFields: Array.isArray(panel.xAxisFields) ? panel.xAxisFields : [],
        series: Array.isArray(panel.series) ? panel.series : [],
        limit: panel.limit ?? DEFAULT_PANEL_CONFIG.limit,
        timeRange: panel.timeRange || 'all',
        timeRangeLast: panel.timeRangeLast || '1h',
        timeRangeStart: panel.timeRangeStart || '',
        timeRangeEnd: panel.timeRangeEnd || '',
        filters: panel.filters || [],
        transformations: panel.transformations || [],
      //  legendPosition: panel.legendPosition || 'top', // ✅ ADD THIS
        
        xAxisLabel: panel.xAxisLabel || '',
        yAxisLabel: panel.yAxisLabel || '',
        xAxisLabelRotation: panel.xAxisLabelRotation ?? 0,
        yAxisLabelRotation: panel.yAxisLabelRotation ?? 0,
        xAxisTickRotation: panel.xAxisTickRotation ?? 0,
        yAxisTickRotation: panel.yAxisTickRotation ?? 0,
        xAxisShowLabel: panel.xAxisShowLabel ?? true,
        yAxisShowLabel: panel.yAxisShowLabel ?? true,
        xAxisShowTicks: panel.xAxisShowTicks ?? true,
        yAxisShowTicks: panel.yAxisShowTicks ?? true,
        
        yAxisNumberFormat: panel.yAxisNumberFormat || 'number',
        yAxisDecimals: panel.yAxisDecimals ?? 2,
        yAxisUnit: panel.yAxisUnit || '',
        yAxisUnitPosition: panel.yAxisUnitPosition || 'suffix',
        yAxisCustomFormat: panel.yAxisCustomFormat || '',
        
        yAxisUseCommas: panel.yAxisUseCommas ?? true,
        
        yAxisWidth: panel.yAxisWidth || 'auto',
        yAxisPosition: panel.yAxisPosition || 'left',
        
        gridStrokeDashArray: panel.gridStrokeDashArray || '3 3',
        gridOpacity: panel.gridOpacity ?? 0.1,
        
        xAxisTickCount: panel.xAxisTickCount || 'auto',
        yAxisTickCount: panel.yAxisTickCount || 'auto',
        xAxisTickInterval: panel.xAxisTickInterval || 'auto',
        
        xAxisLabelFontSize: panel.xAxisLabelFontSize || 12,
        yAxisLabelFontSize: panel.yAxisLabelFontSize || 12,
        xAxisTickFontSize: panel.xAxisTickFontSize || 11,
        yAxisTickFontSize: panel.yAxisTickFontSize || 11
      };
    } else {
      return {
        ...DEFAULT_PANEL_CONFIG,
        id: `panel_${Date.now()}`,
        colors: [COLORS[0], COLORS[1], COLORS[2], COLORS[3], COLORS[4]],
        transformations: [],
        timezone: 'UTC',
        yAxisScale: 'auto',
        yAxisMin: '',
        yAxisMax: '',
        xAxisScale: 'auto',
        xAxisMode: 'timestamp',
        xAxisFields: [],
        series: [],
        timeRange: 'all',
        timeRangeLast: '1h',
        timeRangeStart: '',
        timeRangeEnd: '',
        filters: [],
        dataSource: 'table',
       // legendPosition: 'top', // ✅ ADD THIS
        
        xAxisLabel: '',
        yAxisLabel: '',
        xAxisLabelRotation: 0,
        yAxisLabelRotation: 0,
        xAxisTickRotation: 0,
        yAxisTickRotation: 0,
        xAxisShowLabel: true,
        yAxisShowLabel: true,
        xAxisShowTicks: true,
        yAxisShowTicks: true,
        yAxisNumberFormat: 'number',
        yAxisDecimals: 2,
        yAxisUnit: '',
        yAxisUnitPosition: 'suffix',
        yAxisCustomFormat: '',
        yAxisUseCommas: true,
        yAxisWidth: 'auto',
        yAxisPosition: 'left',
        gridStrokeDashArray: '3 3',
        gridOpacity: 0.1,
        xAxisTickCount: 'auto',
        yAxisTickCount: 'auto',
        xAxisTickInterval: 'auto',
        xAxisLabelFontSize: 12,
        yAxisLabelFontSize: 12,
        xAxisTickFontSize: 11,
        yAxisTickFontSize: 11
      };
    }
  });

  const [previewData, setPreviewData] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [activeTab, setActiveTab] = useState('data');
  const [yFieldSearch, setYFieldSearch] = useState('');
  const [hoveredColor, setHoveredColor] = useState(null);
  const [mysqlTables, setMysqlTables] = useState([]);
  const [mysqlDatabases, setMysqlDatabases] = useState([]);
  // MySQL data sources (registered instances) this dashboard's plant may use.
  const [mysqlInstances, setMysqlInstances] = useState([]);
  const [mysqlSourceMissing, setMysqlSourceMissing] = useState(false);
  // ✅ Memoize theme to prevent recreation
  const theme = useMemo(() => darkMode ? {
    bg: '#0f172a',
    card: '#1e293b',
    cardHover: '#334155',
    text: '#f1f5f9',
    textSecondary: '#cbd5e1',
    textMuted: '#94a3b8',
    border: '#334155',
    borderLight: '#475569',
    accent: '#6366f1',
    accentHover: '#4f46e5',
    shadow: '0 1px 3px rgba(0,0,0,0.3)',
    shadowMd: '0 4px 12px rgba(0,0,0,0.4)',
    shadowLg: '0 10px 40px rgba(0,0,0,0.5)'
  } : {
    bg: '#ffffff',
    card: '#f8fafc',
    cardHover: '#f1f5f9',
    text: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    border: '#e2e8f0',
    borderLight: '#cbd5e1',
    accent: '#6366f1',
    accentHover: '#4f46e5',
    shadow: '0 1px 2px rgba(0,0,0,0.05)',
    shadowMd: '0 4px 6px rgba(0,0,0,0.07)',
    shadowLg: '0 20px 25px rgba(0,0,0,0.1)'
  }, [darkMode]);

  useEffect(() => {
    console.log('📝 Config updated:', config);
  }, [config]);

  useEffect(() => {
    if (config.table && config.dataSource === 'table') {
      fetchTableFields(config.table);
    }
  }, [config.table, config.dbEngine, config.mysqlDatabase, config.mysqlInstanceId]);

  // Which MySQL data source to query, and the plant (tenant) we act under --
  // the server checks the pair.
  const mysqlTarget = { instanceId: config.mysqlInstanceId, plantId };

  // Data sources the dashboard's plant may use, for the picker
  useEffect(() => {
    if (config.dbEngine !== 'mysql' || !plantId) { setMysqlInstances([]); return; }
    mysqlService.listInstances(plantId)
      .then(setMysqlInstances)
      .catch(err => { console.error('MySQL data sources error:', err); setMysqlInstances([]); });
  }, [config.dbEngine, plantId]);

  // Fetch MySQL databases when engine or data source changes
  useEffect(() => {
    if (config.dbEngine === 'mysql') {
      setMysqlSourceMissing(false);
      mysqlService.getDatabases(mysqlTarget).then(result => {
        const dbs = result.map(row => Object.values(row)[0]).filter(db => !['information_schema', 'performance_schema', 'mysql', 'sys'].includes(db));
        setMysqlDatabases(dbs);
      }).catch(err => {
        setMysqlDatabases([]);
        if (mysqlErrorCode(err) === NO_INSTANCE_CODE) setMysqlSourceMissing(true);
        else console.error('MySQL databases error:', err);
      });
    }
  }, [config.dbEngine, config.mysqlInstanceId, plantId]);

  // Fetch MySQL tables when database changes
  useEffect(() => {
    if (config.dbEngine === 'mysql' && config.mysqlDatabase) {
      mysqlService.getTables(config.mysqlDatabase, mysqlTarget).then(result => {
        setMysqlTables(result.map(row => Object.values(row)[0]));
      }).catch(err => console.error('MySQL tables error:', err));
    } else {
      setMysqlTables([]);
    }
  }, [config.dbEngine, config.mysqlDatabase, config.mysqlInstanceId, plantId]);

  const fetchTableFields = async (tableName) => {
    try {
      const isMySQL = config.dbEngine === 'mysql';
      const query = isMySQL
        ? `SELECT * FROM \`${tableName}\` LIMIT 1`
        : `SELECT * FROM ${tableName} LIMIT 1`;
      const result = isMySQL
        ? await mysqlService.query(query, config.mysqlDatabase, mysqlTarget)
        : await questdbService.query(query, plantId);
      if (result.length > 0) {
        const fields = Object.keys(result[0]);
        setAvailableFields(fields);
        // Auto-fix timestampField if current value doesn't exist in this table
        if (!fields.includes(config.timestampField)) {
          const guessed = fields.find(f => /time|date|created|updated/i.test(f)) || fields[0] || '';
          setConfig(prev => ({ ...prev, timestampField: guessed }));
        }
      } else {
        // Table exists but is empty — try to get column names from MySQL DESCRIBE
        if (isMySQL) {
          const descResult = await mysqlService.query(`DESCRIBE \`${tableName}\``, config.mysqlDatabase, mysqlTarget);
          if (descResult.length > 0) {
            const fields = descResult.map(r => r.Field);
            setAvailableFields(fields);
            if (!fields.includes(config.timestampField)) {
              const guessed = fields.find(f => /time|date|created|updated/i.test(f)) || fields[0] || '';
              setConfig(prev => ({ ...prev, timestampField: guessed }));
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching fields:', error);
    }
  };

  const buildQueryWithFiltersAndTimeRange = useCallback(() => {
    let query = config.query;
    const isMySQL = config.dbEngine === 'mysql';

    if (config.dataSource === 'table' && config.table) {
      const tableName = isMySQL ? `\`${config.table}\`` : config.table;
      query = `SELECT * FROM ${tableName}`;

      const whereClauses = [];
      // In simple X-axis mode the X column may not be a timestamp at all, so
      // the time-range filter is meaningless — skip it entirely. Filters
      // (the WHERE-clause UI) still apply because they're column-agnostic.
      const isSimpleXAxis = config.xAxisMode === 'simple';

      if (!isSimpleXAxis && config.timeRange === 'last' && config.timeRangeLast) {
        if (isMySQL) {
          const mysqlIntervals = {
            '5m': '5 MINUTE', '15m': '15 MINUTE', '1h': '1 HOUR',
            '6h': '6 HOUR', '24h': '24 HOUR', '7d': '7 DAY', '30d': '30 DAY'
          };
          const interval = mysqlIntervals[config.timeRangeLast];
          if (interval) {
            whereClauses.push(`\`${config.timestampField}\` >= NOW() - INTERVAL ${interval}`);
          }
        } else {
          const intervals = {
            '5m': '5 minutes', '15m': '15 minutes', '1h': '1 hour',
            '6h': '6 hours', '24h': '24 hours', '7d': '7 days', '30d': '30 days'
          };
          const interval = intervals[config.timeRangeLast];
          if (interval) {
            whereClauses.push(`${config.timestampField} >= dateadd('${interval}', -1, now())`);
          }
        }
      }

      else if (!isSimpleXAxis && config.timeRange === 'custom' && config.timeRangeStart && config.timeRangeEnd) {
        if (isMySQL) {
          whereClauses.push(
            `\`${config.timestampField}\` BETWEEN '${config.timeRangeStart}:00' AND '${config.timeRangeEnd}:00'`
          );
        } else {
          whereClauses.push(
            `${config.timestampField} BETWEEN cast('${config.timeRangeStart}:00' as timestamp) AND cast('${config.timeRangeEnd}:00' as timestamp)`
          );
        }
      }

      if (config.filters && config.filters.length > 0) {
        config.filters.forEach(filter => {
          if (filter.field && filter.operator && filter.value !== '') {
            const isNumber = !isNaN(parseFloat(filter.value)) && isFinite(filter.value);
            const value = isNumber ? filter.value : `'${filter.value.replace(/'/g, "''")}'`;
            const fieldName = isMySQL ? `\`${filter.field}\`` : filter.field;
            whereClauses.push(`${fieldName} ${filter.operator} ${value}`);
          }
        });
      }

      if (whereClauses.length > 0) {
        query += ` WHERE ${whereClauses.join(' AND ')}`;
      }

      if (config.timestampField) {
        const tsField = isMySQL ? `\`${config.timestampField}\`` : config.timestampField;
        // Simple X-axis: ASC reads naturally left-to-right (a→z, 0→9).
        // Timestamp X-axis: keep DESC + LIMIT so we get the most recent N rows.
        query += ` ORDER BY ${tsField} ${isSimpleXAxis ? 'ASC' : 'DESC'}`;
      }
      query += ` LIMIT ${config.limit}`;
    }

    return query;
  }, [config]);

  const formatNumber = useCallback((value) => {
    if (value === null || value === undefined || isNaN(value)) return 'N/A';
    
    const num = parseFloat(value);
    
    switch (config.yAxisNumberFormat) {
      case 'none':
        return value.toString();
        
      case 'number':
        if (config.yAxisUseCommas) {
          const formatted = num.toFixed(config.yAxisDecimals);
          const parts = formatted.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          return parts.join('.');
        }
        return num.toFixed(config.yAxisDecimals);
        
      case 'percent':
        return `${(num).toFixed(config.yAxisDecimals)}%`;
        
      case 'percent_decimal':
        return `${(num * 100).toFixed(config.yAxisDecimals)}%`;
        
      case 'scientific':
        return num.toExponential(config.yAxisDecimals);
        
      case 'bytes':
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        let size = Math.abs(num);
        let unitIndex = 0;
        while (size >= 1024 && unitIndex < units.length - 1) {
          size /= 1024;
          unitIndex++;
        }
        return `${size.toFixed(config.yAxisDecimals)} ${units[unitIndex]}`;
        
      case 'bits':
        const bitUnits = ['bit', 'Kbit', 'Mbit', 'Gbit', 'Tbit', 'Pbit'];
        let bits = Math.abs(num);
        let bitUnitIndex = 0;
        while (bits >= 1000 && bitUnitIndex < bitUnits.length - 1) {
          bits /= 1000;
          bitUnitIndex++;
        }
        return `${bits.toFixed(config.yAxisDecimals)} ${bitUnits[bitUnitIndex]}`;
        
      case 'bps':
        const bpsUnits = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
        let bps = Math.abs(num);
        let bpsUnitIndex = 0;
        while (bps >= 1000 && bpsUnitIndex < bpsUnits.length - 1) {
          bps /= 1000;
          bpsUnitIndex++;
        }
        return `${bps.toFixed(config.yAxisDecimals)} ${bpsUnits[bpsUnitIndex]}`;
        
      case 'Bps':
        const BpsUnits = ['B/s', 'KB/s', 'MB/s', 'GB/s', 'TB/s'];
        let Bps = Math.abs(num);
        let BpsUnitIndex = 0;
        while (Bps >= 1024 && BpsUnitIndex < BpsUnits.length - 1) {
          Bps /= 1024;
          BpsUnitIndex++;
        }
        return `${Bps.toFixed(config.yAxisDecimals)} ${BpsUnits[BpsUnitIndex]}`;
        
      case 'duration_ms':
        const totalSeconds = Math.floor(num / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
        
      case 'duration_s':
        const h = Math.floor(num / 3600);
        const m = Math.floor((num % 3600) / 60);
        const s = Math.floor(num % 60);
        if (h > 0) return `${h}h ${m}m ${s}s`;
        if (m > 0) return `${m}m ${s}s`;
        return `${s}s`;
        
      case 'currency_usd':
        return `$${num.toLocaleString('en-US', { minimumFractionDigits: config.yAxisDecimals, maximumFractionDigits: config.yAxisDecimals })}`;
        
      case 'currency_eur':
        return `€${num.toLocaleString('en-US', { minimumFractionDigits: config.yAxisDecimals, maximumFractionDigits: config.yAxisDecimals })}`;
        
      case 'custom':
        if (config.yAxisCustomFormat) {
          try {
            return config.yAxisCustomFormat.replace('{value}', num.toFixed(config.yAxisDecimals));
          } catch (e) {
            return num.toFixed(config.yAxisDecimals);
          }
        }
        return num.toFixed(config.yAxisDecimals);
        
      default:
        return num.toFixed(config.yAxisDecimals);
    }
  }, [config]);

  const formatTickValue = useCallback((value) => {
    const formatted = formatNumber(value);
    
    if (config.yAxisUnit) {
      if (config.yAxisUnitPosition === 'prefix') {
        return `${config.yAxisUnit}${formatted}`;
      } else {
        return `${formatted}${config.yAxisUnit}`;
      }
    }
    
    return formatted;
  }, [formatNumber, config]);

  // Substitute $variableName / ${variableName} the same way runtime does.
  // Mirrors QuestDBPanel.resolveVariables so preview matches live behaviour.
  const resolveVariables = (query) => {
    if (!query || !dashboardVariables.length) return query;
    let resolved = query;
    dashboardVariables.forEach(variable => {
      let val = variableValues[variable.id];
      if (val === undefined || val === null || val === '') val = variable.defaultValue;
      if ((val === undefined || val === null || val === '') && variable.type === 'slider') {
        val = variable.min ?? 0;
      }
      if (val === undefined || val === null || val === '') return;
      const esc = variable.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      resolved = resolved
        .replace(new RegExp(`\\$\\{${esc}\\}`, 'g'), String(val))
        .replace(new RegExp(`\\$${esc}(?![a-zA-Z0-9_])`, 'g'), String(val));
    });
    return resolved;
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    setPreviewError(null);

    try {
      const rawQuery = buildQueryWithFiltersAndTimeRange();
      const query = resolveVariables(rawQuery);

      if (!query) {
        setPreviewError('Please enter a query or select a table');
        setPreviewLoading(false);
        return;
      }

      const result = config.dbEngine === 'mysql'
        ? await mysqlService.query(query, config.mysqlDatabase, mysqlTarget)
        : await questdbService.query(query, plantId);
      const formatted = questdbService.formatForChart(
        result,
        config.timestampField,
        config.timezone || 'UTC',
        config.xAxisMode || 'timestamp',
        config.yAxis || 'value',
        config.xAxisFields || null
      );
      
      let finalData = formatted;
      if (config.transformations && config.transformations.length > 0) {
        finalData = SimpleTransformations.applyTransformations(formatted, config.transformations);
      }
      
      setPreviewData(finalData);
      
      if (formatted.length > 0 && (!config.yAxes || config.yAxes.length === 0)) {
        const numericFields = Object.keys(formatted[0]).filter(key => 
          typeof formatted[0][key] === 'number' && key !== '_timestamp'
        );
        if (numericFields.length > 0) {
          setConfig({ ...config, yAxis: numericFields[0], yAxes: [numericFields[0]] });
        }
      }
    } catch (error) {
      console.error('Query error:', error);
      setPreviewError(error.message);
    }
    
    setPreviewLoading(false);
  };

  const getColor = useCallback((idx) => {
    if (config.colors && config.colors.length > idx && config.colors[idx]) {
      return config.colors[idx];
    }
    return COLORS[idx % COLORS.length];
  }, [config.colors]);

  const getYAxisDomain = useCallback(() => {
    if (config.yAxisScale === 'custom') {
      const min = config.yAxisMin !== '' ? parseFloat(config.yAxisMin) : 'auto';
      const max = config.yAxisMax !== '' ? parseFloat(config.yAxisMax) : 'auto';
      return [min, max];
    }
    return ['auto', 'auto'];
  }, [config]);

  const addFilter = () => {
    const newFilter = { field: '', operator: '=', value: '' };
    setConfig({
      ...config,
      filters: [...(config.filters || []), newFilter]
    });
  };

  const updateFilter = (index, key, value) => {
    const newFilters = [...config.filters];
    newFilters[index][key] = value;
    setConfig({ ...config, filters: newFilters });
  };

  const removeFilter = (index) => {
    const newFilters = config.filters.filter((_, i) => i !== index);
    setConfig({ ...config, filters: newFilters });
  };

  const renderPreviewChart = () => {
    if (previewLoading) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px' }}>
          <RefreshCw size={20} color={theme.accent} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      );
    }

    if (previewError) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '200px',
          color: '#ef4444',
          textAlign: 'center',
          padding: '20px',
          fontSize: '13px'
        }}>
          <div>
            <AlertCircle size={24} style={{ marginBottom: '8px' }} />
            <div>{previewError}</div>
          </div>
        </div>
      );
    }

    if (previewData.length === 0) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '200px',
          color: theme.textMuted,
          fontSize: '13px'
        }}>
          Click "Preview" to test your configuration
        </div>
      );
    }

    const chartProps = { 
      data: previewData, 
      margin: { top: 5, right: 20, left: 10, bottom: 5 } 
    };
    const yFields = (config.yAxes && config.yAxes.length > 0) ? config.yAxes : [config.yAxis].filter(Boolean);
    const yDomain = getYAxisDomain();

    const xAxisConfig = {
      dataKey: "_time",
      tick: config.xAxisShowTicks ? { 
        fill: theme.textMuted, 
        fontSize: config.xAxisTickFontSize,
        angle: config.xAxisTickRotation,
        textAnchor: config.xAxisTickRotation !== 0 ? 'end' : 'middle'
      } : false,
      label: config.xAxisShowLabel && config.xAxisLabel ? {
        value: config.xAxisLabel,
        position: 'insideBottom',
        offset: -5,
        fontSize: config.xAxisLabelFontSize,
        angle: config.xAxisLabelRotation
      } : undefined,
      tickCount: config.xAxisTickCount !== 'auto' ? parseInt(config.xAxisTickCount) : undefined,
      interval: config.xAxisTickInterval !== 'auto' ? parseInt(config.xAxisTickInterval) : undefined
    };

    const yAxisConfig = {
      tick: config.yAxisShowTicks ? { 
        fill: theme.textMuted, 
        fontSize: config.yAxisTickFontSize,
        angle: config.yAxisTickRotation,
        textAnchor: config.yAxisTickRotation !== 0 ? 'end' : 'end'
      } : false,
      tickFormatter: formatTickValue,
      label: config.yAxisShowLabel && config.yAxisLabel ? {
        value: config.yAxisLabel,
        angle: -90,
        position: 'insideLeft',
        fontSize: config.yAxisLabelFontSize,
        angle: config.yAxisLabelRotation !== 0 ? config.yAxisLabelRotation : -90
      } : undefined,
      domain: yDomain,
      scale: config.yAxisScale === 'log' ? 'log' : config.yAxisScale === 'linear' ? 'linear' : 'auto',
      width: config.yAxisWidth !== 'auto' ? parseInt(config.yAxisWidth) : undefined,
      orientation: config.yAxisPosition,
      tickCount: config.yAxisTickCount !== 'auto' ? parseInt(config.yAxisTickCount) : undefined
    };

    const gridConfig = config.showGrid ? {
      strokeDasharray: config.gridStrokeDashArray,
      stroke: `rgba(0,0,0,${config.gridOpacity})`
    } : false;

    // ✅ Legend config based on position
    const legendConfig = config.showLegend ? {
      wrapperStyle: { 
        color: theme.text, 
        fontSize: '11px',
        paddingTop: config.legendPosition === 'top' ? '5px' : '0',
        paddingBottom: config.legendPosition === 'bottom' ? '5px' : '0'
      },
      layout: (config.legendPosition === 'left' || config.legendPosition === 'right') 
        ? 'vertical' 
        : 'horizontal',
      verticalAlign: (config.legendPosition === 'top' || config.legendPosition === 'bottom') 
        ? config.legendPosition 
        : 'middle',
      align: (config.legendPosition === 'left' || config.legendPosition === 'right') 
        ? config.legendPosition 
        : 'center',
      iconSize: 8
    } : null;

    const tooltipStyle = { background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 11, color: theme.text };
    const tooltipItemStyle = { color: theme.text };
    const tooltipLabelStyle = { color: theme.textMuted, marginBottom: 2 };

    const renderChart = () => {
      switch (config.vizType) {
        case 'line':
          return (
            <LineChart {...chartProps}>
              {gridConfig && <CartesianGrid {...gridConfig} />}
              <XAxis {...xAxisConfig} />
              <YAxis {...yAxisConfig} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
              {legendConfig && <Legend {...legendConfig} />}
              {yFields.map((yField, idx) => (
                <Line key={yField} type="monotone" dataKey={yField} stroke={getColor(idx)} strokeWidth={config.lineWidth} dot={config.showDots} name={yField} />
              ))}
            </LineChart>
          );

        case 'area':
          return (
            <AreaChart {...chartProps}>
              {gridConfig && <CartesianGrid {...gridConfig} />}
              <XAxis {...xAxisConfig} />
              <YAxis {...yAxisConfig} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
              {legendConfig && <Legend {...legendConfig} />}
              {yFields.map((yField, idx) => (
                <Area key={yField} type="monotone" dataKey={yField} stroke={getColor(idx)} fill={getColor(idx)} fillOpacity={config.fillOpacity} strokeWidth={config.lineWidth} name={yField} />
              ))}
            </AreaChart>
          );

        case 'bar':
          return (
            <BarChart {...chartProps}>
              {gridConfig && <CartesianGrid {...gridConfig} />}
              <XAxis {...xAxisConfig} />
              <YAxis {...yAxisConfig} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
              {legendConfig && <Legend {...legendConfig} />}
              {yFields.map((yField, idx) => (
                <Bar key={yField} dataKey={yField} fill={getColor(idx)} name={yField} />
              ))}
            </BarChart>
          );

        case 'scatter': {
          const xField = config.xAxis || '_time';
          const yField = yFields[0] || Object.keys(previewData[0] || {}).find(k => k !== xField && k !== '_time') || '';
          return (
            <ScatterChart {...chartProps}>
              {gridConfig && <CartesianGrid {...gridConfig} />}
              <XAxis dataKey={xField} tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <YAxis dataKey={yField} tick={{ fill: theme.textMuted, fontSize: 10 }} tickFormatter={formatTickValue} />
              <Tooltip cursor={{ strokeDasharray: '3 3' }} contentStyle={tooltipStyle} />
              {legendConfig && <Legend {...legendConfig} />}
              <Scatter name={yField} data={previewData} fill={getColor(0)} />
            </ScatterChart>
          );
        }

        case 'pie': {
          const pieField = yFields[0] || Object.keys(previewData[0] || {}).find(k => !['_time', 'timestamp'].includes(k)) || '';
          const nameField = config.xAxis || '_time';
          // Group by nameField if it's a string category, else just use index
          const pieData = previewData.slice(0, 8).map((row, i) => {
            const rawName = row[nameField];
            const isTimestamp = typeof rawName === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(rawName);
            return {
              name: isTimestamp ? `#${i + 1}` : String(rawName || i + 1).slice(0, 10),
              value: typeof row[pieField] === 'number' ? row[pieField] : parseFloat(row[pieField]) || 0
            };
          });
          const RADIAN = Math.PI / 180;
          const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
            if (percent < 0.05) return null;
            const r = innerRadius + (outerRadius - innerRadius) * 0.5;
            const x = cx + r * Math.cos(-midAngle * RADIAN);
            const y = cy + r * Math.sin(-midAngle * RADIAN);
            return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={600}>{`${(percent * 100).toFixed(0)}%`}</text>;
          };
          return (
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} labelLine={false} label={renderCustomLabel}>
                {pieData.map((_, idx) => <Cell key={idx} fill={getColor(idx)} />)}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} formatter={(val) => [formatTickValue(val), pieField]} />
              <Legend wrapperStyle={{ color: theme.text, fontSize: 11 }} />
            </PieChart>
          );
        }

        case 'radar': {
          const radarFields = yFields.length > 0 ? yFields : Object.keys(previewData[0] || {}).filter(k => k !== '_time').slice(0, 3);
          const subjectField = config.xAxis || '_time';
          const radarData = previewData.slice(0, 8).map(row => {
            const entry = { subject: String(row[subjectField] || '').slice(0, 10) };
            radarFields.forEach(f => { entry[f] = typeof row[f] === 'number' ? row[f] : parseFloat(row[f]) || 0; });
            return entry;
          });
          return (
            <RadarChart cx="50%" cy="50%" outerRadius={80} data={radarData} width={400} height={200}>
              <PolarGrid stroke={theme.border} />
              <PolarAngleAxis dataKey="subject" tick={{ fill: theme.textMuted, fontSize: 10 }} />
              <PolarRadiusAxis tick={{ fill: theme.textMuted, fontSize: 9 }} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={tooltipItemStyle} labelStyle={tooltipLabelStyle} />
              {legendConfig && <Legend {...legendConfig} />}
              {radarFields.map((f, idx) => (
                <Radar key={f} name={f} dataKey={f} stroke={getColor(idx)} fill={getColor(idx)} fillOpacity={0.2} />
              ))}
            </RadarChart>
          );
        }

        case 'stat': {
          const statField = yFields[0] || Object.keys(previewData[0] || {}).find(k => k !== '_time') || '';
          const lastRow = previewData[previewData.length - 1] || {};
          const statValue = lastRow[statField];
          const formatted = typeof statValue === 'number' ? formatTickValue(statValue) : String(statValue ?? '—');
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200 }}>
              <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{statField || 'Value'}</div>
              <div style={{ fontSize: 48, fontWeight: 700, color: getColor(0), lineHeight: 1 }}>{formatted}</div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8 }}>{previewData.length} data points</div>
            </div>
          );
        }

        case 'table': {
          const cols = Object.keys(previewData[0] || {}).filter(k => !k.startsWith('_')).slice(0, 6);
          return (
            <div style={{ height: 200, overflow: 'auto', fontSize: 11 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', position: 'sticky', top: 0 }}>
                    {cols.map(c => <th key={c} style={{ padding: '5px 8px', textAlign: 'left', color: theme.textMuted, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 10).map((row, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') }}>
                      {cols.map(c => <td key={c} style={{ padding: '4px 8px', color: typeof row[c] === 'number' ? theme.accent : theme.text, fontWeight: typeof row[c] === 'number' ? 600 : 400, whiteSpace: 'nowrap' }}>{typeof row[c] === 'number' ? formatTickValue(row[c]) : String(row[c] ?? '—').slice(0, 20)}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }

        default:
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: theme.textMuted, fontSize: 13 }}>
              Preview not available for this type
            </div>
          );
      }
    };

    if (config.vizType === 'stat' || config.vizType === 'table') {
      return renderChart();
    }

    return (
      <ResponsiveContainer width="100%" height={200}>
        {renderChart()}
      </ResponsiveContainer>
    );
  };

  const renderDataTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', minHeight: '500px' }}>
      <div>
        <Input
          label="Panel Title"
          value={config.title}
          onChange={(e) => setConfig({ ...config, title: e.target.value })}
          placeholder="My Dashboard Panel"
          theme={theme}
        />

        {/* Visualization Type - Custom dropdown with icons */}
        <VizTypeDropdown
          value={config.vizType}
          onChange={(id) => setConfig({ ...config, vizType: id })}
          theme={theme}
        />

        {/* Database Engine Toggle */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '11px',
            fontWeight: '500',
            color: theme.textMuted,
            letterSpacing: '0.02em'
          }}>
            Database Engine
          </label>
          <div style={{ display: 'inline-flex', gap: '4px', marginBottom: '10px', padding: '3px', background: theme.card, borderRadius: '6px' }}>
            {[
              { id: 'questdb', label: 'QuestDB' },
              { id: 'mysql', label: 'MySQL' }
            ].map(engine => (
              <button
                key={engine.id}
                onClick={() => setConfig({ ...config, dbEngine: engine.id, table: '', mysqlDatabase: config.mysqlDatabase || '' })}
                style={{
                  padding: '6px 14px',
                  background: config.dbEngine === engine.id ? theme.bg : 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  color: config.dbEngine === engine.id ? theme.text : theme.textMuted,
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500',
                  transition: 'all 0.15s ease',
                  boxShadow: config.dbEngine === engine.id ? theme.shadow : 'none'
                }}
              >
                <Database size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                {engine.label}
              </button>
            ))}
          </div>

          {/* MySQL data source (registered instance) picker */}
          {config.dbEngine === 'mysql' && (
            <Select
              label="MySQL Data Source"
              value={config.mysqlInstanceId || ''}
              onChange={(e) => setConfig({ ...config, mysqlInstanceId: e.target.value, mysqlDatabase: '', table: '' })}
              theme={theme}
              data-testid="mysql-instance-picker"
            >
              <option value="">Server default connection</option>
              {mysqlInstances.map(inst => (
                <option key={inst.instanceId} value={inst.instanceId}>
                  {inst.label || inst.instanceId}{inst.isReadReplica ? ' (read replica)' : ''}
                </option>
              ))}
              {config.mysqlInstanceId && !mysqlInstances.some(i => i.instanceId === config.mysqlInstanceId) && (
                <option value={config.mysqlInstanceId}>{config.mysqlInstanceId} (unavailable)</option>
              )}
            </Select>
          )}
          {config.dbEngine === 'mysql' && mysqlSourceMissing && (
            <div data-testid="mysql-source-missing" style={{ fontSize: '12px', color: '#f59e0b', margin: '-4px 0 12px' }}>
              Select a data source — this server has no default MySQL connection.
            </div>
          )}

          {/* MySQL Database Selector */}
          {config.dbEngine === 'mysql' && (
            <Select
              label="Database"
              value={config.mysqlDatabase}
              onChange={(e) => setConfig({ ...config, mysqlDatabase: e.target.value, table: '' })}
              theme={theme}
            >
              <option value="">Select a database...</option>
              {mysqlDatabases.map(db => (
                <option key={db} value={db}>{db}</option>
              ))}
            </Select>
          )}
        </div>

        {/* Data Source - Inline Toggle */}
        <div style={{ marginBottom: '16px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '11px',
            fontWeight: '500',
            color: theme.textMuted,
            letterSpacing: '0.02em'
          }}>
            Data Source
          </label>
          <div style={{ display: 'inline-flex', gap: '4px', marginBottom: '10px', padding: '3px', background: theme.card, borderRadius: '6px' }}>
            <button
              onClick={() => setConfig({ ...config, dataSource: 'table' })}
              style={{
                padding: '6px 14px',
                background: config.dataSource === 'table' ? theme.bg : 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: config.dataSource === 'table' ? theme.text : theme.textMuted,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                boxShadow: config.dataSource === 'table' ? theme.shadow : 'none'
              }}
            >
              <Database size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
              Table
            </button>
            <button
              onClick={() => setConfig({ ...config, dataSource: 'custom' })}
              style={{
                padding: '6px 14px',
                background: config.dataSource === 'custom' ? theme.bg : 'transparent',
                border: 'none',
                borderRadius: '4px',
                color: config.dataSource === 'custom' ? theme.text : theme.textMuted,
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                transition: 'all 0.15s ease',
                boxShadow: config.dataSource === 'custom' ? theme.shadow : 'none'
              }}
            >
              Custom SQL
            </button>
          </div>

          {config.dataSource === 'table' ? (
            <Select
              value={config.table}
              onChange={(e) => setConfig({ ...config, table: e.target.value })}
              theme={theme}
            >
              <option value="">Select a table...</option>
              {(config.dbEngine === 'mysql' ? mysqlTables : (allTables || [])).map(table => (
                <option key={table} value={table}>{table}</option>
              ))}
            </Select>
          ) : (
            <textarea
              value={config.query}
              onChange={(e) => setConfig({ ...config, query: e.target.value })}
              rows={3}
              placeholder="SELECT * FROM your_table WHERE condition ORDER BY timestamp DESC LIMIT 100"
              style={{
                width: '100%',
                padding: '8px 10px',
                background: theme.bg,
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                color: theme.text,
                fontSize: '12px',
                fontFamily: 'ui-monospace, monospace',
                resize: 'vertical',
                outline: 'none',
                transition: 'all 0.15s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = theme.accent}
              onBlur={(e) => e.target.style.borderColor = theme.border}
            />
          )}
        </div>

        {/* Scenario adjustments — declarative what-if without editing SQL.
            User picks column + operator + slider variable. Applied client-
            side so slider drags update instantly. */}
        {(() => {
          const scenarios = Array.isArray(config.scenarioAdjustments) ? config.scenarioAdjustments : [];
          const sliderVars = (dashboardVariables || []).filter(v => v.type === 'slider');
          const previewCols = (previewData && previewData.length)
            ? Object.keys(previewData[0]).filter(k => !k.startsWith('_'))
            : [];

          const updateRule = (i, patch) => {
            const next = scenarios.map((r, j) => j === i ? { ...r, ...patch } : r);
            setConfig({ ...config, scenarioAdjustments: next });
          };
          const removeRule = (i) => {
            const next = scenarios.filter((_, j) => j !== i);
            setConfig({ ...config, scenarioAdjustments: next });
          };
          const addRule = () => {
            const next = [...scenarios, {
              id: `sa_${Date.now()}`,
              column: previewCols[0] || '',
              op: '*',
              variableName: sliderVars[0]?.name || ''
            }];
            setConfig({ ...config, scenarioAdjustments: next });
          };

          return (
            <div style={{ marginBottom: 16 }}>
              <label style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                fontSize: 11, fontWeight: 500, color: theme.textMuted, marginBottom: 6
              }}>
                <span>Scenario adjustments (what-if)</span>
                {sliderVars.length === 0 && (
                  <span style={{ fontSize: 10, color: theme.textMuted, fontWeight: 400 }}>
                    Create a slider variable first in the Variables manager
                  </span>
                )}
              </label>
              {scenarios.map((rule, i) => (
                <div key={rule.id || i} style={{
                  display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6
                }}>
                  {/* Column: dropdown if we ran preview, else text input */}
                  {previewCols.length > 0 ? (
                    <select
                      value={rule.column || ''}
                      onChange={(e) => updateRule(i, { column: e.target.value })}
                      style={{
                        flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12,
                        background: theme.bg, color: theme.text,
                        border: `1px solid ${theme.border}`, borderRadius: 4
                      }}
                    >
                      <option value="">— pick column —</option>
                      {previewCols.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  ) : (
                    <input
                      value={rule.column || ''}
                      onChange={(e) => updateRule(i, { column: e.target.value })}
                      placeholder="column name"
                      style={{
                        flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12,
                        background: theme.bg, color: theme.text,
                        border: `1px solid ${theme.border}`, borderRadius: 4
                      }}
                    />
                  )}

                  <select
                    value={rule.op || '*'}
                    onChange={(e) => updateRule(i, { op: e.target.value })}
                    style={{
                      width: 76, padding: '5px 6px', fontSize: 12,
                      background: theme.bg, color: theme.text,
                      border: `1px solid ${theme.border}`, borderRadius: 4
                    }}
                    title="How the slider adjusts the column"
                  >
                    <option value="*">× multiply</option>
                    <option value="+">+ add</option>
                    <option value="-">− subtract</option>
                    <option value="pct">% pct</option>
                  </select>

                  <select
                    value={rule.variableName || ''}
                    onChange={(e) => updateRule(i, { variableName: e.target.value })}
                    disabled={sliderVars.length === 0}
                    style={{
                      flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12,
                      background: theme.bg, color: theme.text,
                      border: `1px solid ${theme.border}`, borderRadius: 4
                    }}
                  >
                    <option value="">— pick slider —</option>
                    {sliderVars.map(v => (
                      <option key={v.id} value={v.name}>${v.name}{v.label ? ` (${v.label})` : ''}</option>
                    ))}
                  </select>

                  <button
                    onClick={() => removeRule(i)}
                    style={{
                      background: 'transparent', border: 'none',
                      color: '#f87171', cursor: 'pointer',
                      padding: '3px 6px', fontSize: 14
                    }}
                    title="Remove this adjustment"
                  >×</button>
                </div>
              ))}
              <button
                onClick={addRule}
                disabled={sliderVars.length === 0}
                title={sliderVars.length === 0 ? 'Create a slider variable first' : 'Add a what-if adjustment'}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: 12, fontWeight: 500,
                  background: 'transparent',
                  border: `1px dashed ${sliderVars.length === 0 ? theme.border : theme.accent}`,
                  color: sliderVars.length === 0 ? theme.textMuted : theme.accent,
                  borderRadius: 4,
                  cursor: sliderVars.length === 0 ? 'not-allowed' : 'pointer',
                  opacity: sliderVars.length === 0 ? 0.6 : 1
                }}
              >+ Add adjustment</button>
              {scenarios.length > 0 && (
                <div style={{
                  fontSize: 10, color: theme.textMuted, marginTop: 4, lineHeight: 1.4
                }}>
                  Adjustments apply to the fetched data before rendering — no SQL editing needed.
                </div>
              )}
            </div>
          );
        })()}

        {/* Field Configuration - Improved for many columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px', marginBottom: '16px' }}>
          {/* X-Axis mode toggle: timestamp (time-series with time-range filter)
              vs simple (any column, no time-range). Default is timestamp for
              back-compat with existing panels. */}
          <div>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: '500',
              color: theme.textMuted,
              marginBottom: '6px',
              letterSpacing: '0.02em'
            }}>
              X-Axis Mode
            </label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[
                { id: 'timestamp', label: 'Time Series' },
                { id: 'simple',    label: 'Simple X-Axis' }
              ].map(opt => {
                const active = config.xAxisMode === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => setConfig({ ...config, xAxisMode: opt.id })}
                    style={{
                      flex: 1,
                      padding: '6px 10px',
                      background: active ? theme.accent : theme.card,
                      color: active ? '#fff' : theme.text,
                      border: `1px solid ${active ? theme.accent : theme.border}`,
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div style={{ fontSize: '10px', color: theme.textMuted, marginTop: '4px' }}>
              {config.xAxisMode === 'simple'
                ? 'Pick any column for the X-axis. Time-range filter is disabled.'
                : 'X-axis is bound to a timestamp column. Time-range filter applies.'}
            </div>
          </div>

          {config.xAxisMode === 'simple' ? (
            <div>
              <label style={{
                fontSize: '11px', fontWeight: '500', color: theme.textMuted,
                letterSpacing: '0.02em', display: 'block', marginBottom: 6
              }}>
                X-Axis Field(s) — pick one or more (labels joined with —)
              </label>
              <div style={{
                border: `1px solid ${theme.border}`,
                borderRadius: '6px',
                background: theme.bg,
                maxHeight: '180px',
                overflow: 'auto',
                padding: '4px'
              }}>
                {availableFields.length === 0 ? (
                  <div style={{ fontSize: 11, color: theme.textMuted, fontStyle: 'italic', padding: '6px 8px' }}>
                    Run preview to load fields.
                  </div>
                ) : availableFields.map(field => {
                  const list = Array.isArray(config.xAxisFields) ? config.xAxisFields : [];
                  const checked = list.includes(field);
                  const order = checked ? list.indexOf(field) + 1 : 0;
                  return (
                    <label
                      key={field}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '5px 8px', cursor: 'pointer', borderRadius: '4px',
                        fontSize: '12px', color: theme.text,
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = theme.cardHover}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...list, field]
                            : list.filter(f => f !== field);
                          setConfig({
                            ...config,
                            xAxisFields: next,
                            // keep primary in sync so legacy code keeps working
                            timestampField: next[0] || config.timestampField
                          });
                        }}
                        style={{ width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{ flex: 1 }}>{field}</span>
                      {checked && (
                        <span style={{
                          fontSize: 10, color: theme.textMuted,
                          padding: '1px 6px', borderRadius: 8, background: theme.cardHover
                        }}>
                          {order}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {(config.xAxisFields || []).length > 0 && (
                <div style={{ marginTop: '6px', fontSize: '10px', color: theme.textMuted }}>
                  {config.xAxisFields.length} field{config.xAxisFields.length !== 1 ? 's' : ''} selected
                  {config.xAxisFields.length > 1 && <> · joined with —</>}
                </div>
              )}
            </div>
          ) : (
            <Select
              label="Timestamp Field"
              value={config.timestampField}
              onChange={(e) => setConfig({ ...config, timestampField: e.target.value })}
              theme={theme}
            >
              {availableFields.length > 0 ? (
                availableFields.map(field => (
                  <option key={field} value={field}>{field}</option>
                ))
              ) : (
                <option value={config.timestampField || 'timestamp'}>{config.timestampField || 'timestamp'}</option>
              )}
            </Select>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <label style={{ 
                fontSize: '11px', 
                fontWeight: '500',
                color: theme.textMuted,
                letterSpacing: '0.02em'
              }}>
                Value Fields (Y-Axis)
              </label>
              {availableFields.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', fontSize: '10px' }}>
                  <button
                    onClick={() => {
                      const numericFields = availableFields.filter(f => f !== config.timestampField);
                      setConfig({ ...config, yAxes: numericFields, yAxis: numericFields[0] || '' });
                    }}
                    style={{
                      padding: '3px 8px',
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                      color: theme.textMuted,
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme.text}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setConfig({ ...config, yAxes: [], yAxis: '' })}
                    style={{
                      padding: '3px 8px',
                      background: 'transparent',
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                      color: theme.textMuted,
                      cursor: 'pointer',
                      fontWeight: '500',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = theme.text}
                    onMouseLeave={(e) => e.currentTarget.style.color = theme.textMuted}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            {availableFields.length > 0 ? (
  <div>
    <input
      type="text"
      value={yFieldSearch}
      onChange={(e) => setYFieldSearch(e.target.value)}
      placeholder="Search fields..."
      style={{
        width: '100%',
        padding: '6px 10px',
        marginBottom: '6px',
        background: theme.bg,
        border: `1px solid ${theme.border}`,
        borderRadius: '6px',
        color: theme.text,
        fontSize: '12px',
        outline: 'none',
        boxSizing: 'border-box'
      }}
      onFocus={(e) => e.target.style.borderColor = theme.accent}
      onBlur={(e) => e.target.style.borderColor = theme.border}
    />
  <div style={{
    border: `1px solid ${theme.border}`,
    borderRadius: '6px',
    background: theme.bg,
    maxHeight: '180px',
    overflow: 'auto',
    padding: '4px'
  }}>
    {availableFields
      .filter(f => f !== config.timestampField)
      .filter(f => f.toLowerCase().includes(yFieldSearch.toLowerCase()))
      .map((field) => (
                  <label 
                    key={field}
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      padding: '5px 8px',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: theme.text,
                      transition: 'background 0.15s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = theme.cardHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <input
                      type="checkbox"
                      checked={config.yAxes?.includes(field)}
                      onChange={(e) => {
                        const newYAxes = e.target.checked
                          ? [...(config.yAxes || []), field]
                          : (config.yAxes || []).filter(y => y !== field);
                        setConfig({ ...config, yAxes: newYAxes, yAxis: newYAxes[0] || '' });
                      }}
                      style={{ width: '14px', height: '14px', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <span style={{ flex: 1 }}>{field}</span>
                    {config.yAxes?.includes(field) && (
                      <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        background: getColor(config.yAxes.indexOf(field)),
                        flexShrink: 0
                      }} />
                    )}
                  </label>
                ))}
              </div>
               </div> 
            ) : (
              <Input
                value={(config.yAxes || []).join(', ')}
                onChange={(e) => setConfig({ 
                  ...config, 
                  yAxes: e.target.value.split(',').map(s => s.trim()).filter(Boolean),
                  yAxis: e.target.value.split(',')[0]?.trim() || ''
                })}
                placeholder="value1, value2"
                theme={theme}
              />
            )}
            {config.yAxes && config.yAxes.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '10px', color: theme.textMuted }}>
                {config.yAxes.length} field{config.yAxes.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        </div>

        {/* ───── Compare Series — one query per series, built from filters ───── */}
        <div style={{ marginTop: 14, padding: 10, border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.bg }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: theme.text, letterSpacing: '0.02em' }}>
              Compare Series ({(config.series || []).length})
            </label>
            <button
              onClick={() => {
                const palette = ['#22d3ee', '#fb7185', '#a78bfa', '#facc15', '#34d399', '#f97316'];
                const next = [...(config.series || []), {
                  id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                  name: `Series ${(config.series || []).length + 1}`,
                  filters: [],
                  color: palette[(config.series || []).length % palette.length],
                  yField: (config.yAxes && config.yAxes[0]) || config.yAxis || 'value'
                }];
                setConfig({ ...config, series: next });
              }}
              style={{
                padding: '3px 10px', fontSize: 11, borderRadius: 4,
                background: theme.accent, color: '#fff', border: 'none', cursor: 'pointer'
              }}
            >+ Add Series</button>
          </div>
          <div style={{ fontSize: 10, color: theme.textMuted, marginBottom: 8 }}>
            Each series inherits the panel's table, timestamp, time-range and limit.
            Just pick the Y column and add WHERE filters.
          </div>

          {(config.series || []).map((s, idx) => {
            const updateSeries = (patch) => {
              const next = [...config.series];
              next[idx] = { ...s, ...patch };
              setConfig({ ...config, series: next });
            };
            const updateFilter = (fi, key, val) => {
              const fl = [...(s.filters || [])];
              fl[fi] = { ...fl[fi], [key]: val };
              updateSeries({ filters: fl });
            };
            const removeFilter = (fi) => {
              updateSeries({ filters: (s.filters || []).filter((_, i) => i !== fi) });
            };
            const addFilterRow = () => {
              updateSeries({ filters: [...(s.filters || []), { field: '', operator: '=', value: '' }] });
            };
            return (
              <div key={s.id || idx} style={{
                padding: 8, marginBottom: 6, borderRadius: 4,
                background: theme.card, border: `1px solid ${theme.border}`
              }}>
                {/* Series header row */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                  <input
                    type="color"
                    value={s.color || '#22d3ee'}
                    onChange={(e) => updateSeries({ color: e.target.value })}
                    style={{ width: 30, height: 24, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                  />
                  <input
                    value={s.name || ''}
                    onChange={(e) => updateSeries({ name: e.target.value })}
                    placeholder="Legend label"
                    style={{
                      flex: 1, padding: '4px 8px', fontSize: 12, borderRadius: 4,
                      background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, outline: 'none'
                    }}
                  />
                  <select
                    value={s.yField || ''}
                    onChange={(e) => updateSeries({ yField: e.target.value })}
                    style={{
                      width: 130, padding: '4px 8px', fontSize: 11, borderRadius: 4,
                      background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, outline: 'none'
                    }}
                  >
                    <option value="">Y field…</option>
                    {availableFields.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <button
                    onClick={() => updateSeries.__remove ? null : setConfig({ ...config, series: config.series.filter((_, i) => i !== idx) })}
                    title="Remove series"
                    style={{
                      padding: '4px 8px', fontSize: 11, borderRadius: 4,
                      background: 'transparent', color: '#f87171',
                      border: `1px solid ${theme.border}`, cursor: 'pointer'
                    }}
                  >×</button>
                </div>

                {/* Filters editor */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {(s.filters || []).map((f, fi) => (
                    <div key={fi} style={{
                      display: 'grid', gridTemplateColumns: '1fr 0.6fr 1fr auto', gap: 6
                    }}>
                      <select
                        value={f.field}
                        onChange={(e) => updateFilter(fi, 'field', e.target.value)}
                        style={{
                          padding: '4px', fontSize: 11, borderRadius: 4,
                          background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, outline: 'none'
                        }}
                      >
                        <option value="">Field…</option>
                        {availableFields.map(field => <option key={field} value={field}>{field}</option>)}
                      </select>
                      <select
                        value={f.operator || '='}
                        onChange={(e) => updateFilter(fi, 'operator', e.target.value)}
                        style={{
                          padding: '4px', fontSize: 11, borderRadius: 4,
                          background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, outline: 'none'
                        }}
                      >
                        <option value="=">=</option>
                        <option value="!=">!=</option>
                        <option value=">">&gt;</option>
                        <option value="<">&lt;</option>
                        <option value=">=">&gt;=</option>
                        <option value="<=">&lt;=</option>
                        <option value="LIKE">LIKE</option>
                      </select>
                      <input
                        value={f.value}
                        onChange={(e) => updateFilter(fi, 'value', e.target.value)}
                        placeholder="Value"
                        style={{
                          padding: '4px 6px', fontSize: 11, borderRadius: 4,
                          background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, outline: 'none'
                        }}
                      />
                      <button
                        onClick={() => removeFilter(fi)}
                        style={{
                          padding: '2px 6px', fontSize: 11, borderRadius: 4,
                          background: 'transparent', color: '#ef4444',
                          border: `1px solid ${theme.border}`, cursor: 'pointer'
                        }}
                      >×</button>
                    </div>
                  ))}
                  <button
                    onClick={addFilterRow}
                    style={{
                      marginTop: 2, padding: '3px 8px', fontSize: 10, alignSelf: 'flex-start',
                      background: 'transparent', color: theme.textMuted,
                      border: `1px dashed ${theme.border}`, borderRadius: 4, cursor: 'pointer'
                    }}
                  >+ Add filter</button>
                </div>
              </div>
            );
          })}
        </div>

        {config.xAxisMode !== 'simple' && (
          <Select
            label={<><Globe size={11} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />Timezone</>}
            value={config.timezone || 'UTC'}
            onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
            theme={theme}
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </Select>
        )}
      </div>

      {/* Right Column - Preview & Filters */}
      <div>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ 
              fontSize: '11px', 
              fontWeight: '500',
              color: theme.textMuted,
              letterSpacing: '0.02em'
            }}>
              Preview
            </label>
            <button
              onClick={handlePreview}
              disabled={previewLoading}
              style={{
                padding: '5px 12px',
                background: theme.accent,
                border: 'none',
                borderRadius: '5px',
                color: 'white',
                cursor: previewLoading ? 'not-allowed' : 'pointer',
                fontSize: '11px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                transition: 'all 0.15s ease',
                opacity: previewLoading ? 0.6 : 1
              }}
              onMouseEnter={(e) => !previewLoading && (e.currentTarget.style.background = theme.accentHover)}
              onMouseLeave={(e) => !previewLoading && (e.currentTarget.style.background = theme.accent)}
            >
              <Eye size={12} />
              {previewLoading ? 'Loading...' : 'Preview'}
            </button>
          </div>
          <div style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '8px',
            padding: '12px',
            minHeight: '200px'
          }}>
            {renderPreviewChart()}
          </div>
        </div>

        {/* Time Range — only meaningful when X-axis is bound to a timestamp.
            In simple X-axis mode the user picks any column, so a time-range
            filter wouldn't apply. */}
        {config.xAxisMode !== 'simple' && (
        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontSize: '11px', 
            fontWeight: '500',
            color: theme.textMuted,
            letterSpacing: '0.02em'
          }}>
            Time Range
          </label>
          <div style={{ display: 'inline-flex', gap: '4px', marginBottom: '10px', padding: '3px', background: theme.card, borderRadius: '6px', width: '100%' }}>
            {['all', 'last', 'custom'].map(range => (
              <button
                key={range}
                onClick={() => setConfig({ ...config, timeRange: range })}
                style={{
                  flex: 1,
                  padding: '5px',
                  background: config.timeRange === range ? theme.bg : 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  color: config.timeRange === range ? theme.text : theme.textMuted,
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '500',
                  textTransform: 'capitalize',
                  transition: 'all 0.15s ease',
                  boxShadow: config.timeRange === range ? theme.shadow : 'none'
                }}
              >
                {range}
              </button>
            ))}
          </div>
          
          {config.timeRange === 'last' && (
            <Select
              value={config.timeRangeLast}
              onChange={(e) => setConfig({ ...config, timeRangeLast: e.target.value })}
              theme={theme}
            >
              <option value="5m">Last 5 minutes</option>
              <option value="15m">Last 15 minutes</option>
              <option value="1h">Last 1 hour</option>
              <option value="6h">Last 6 hours</option>
              <option value="24h">Last 24 hours</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </Select>
          )}
          
          {config.timeRange === 'custom' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Input
                label="Start"
                type="datetime-local"
                value={config.timeRangeStart}
                onChange={(e) => setConfig({ ...config, timeRangeStart: e.target.value })}
                theme={theme}
              />
              <Input
                label="End"
                type="datetime-local"
                value={config.timeRangeEnd}
                onChange={(e) => setConfig({ ...config, timeRangeEnd: e.target.value })}
                theme={theme}
              />
            </div>
          )}
        </div>
        )}

        {/* Data Filters */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ 
              fontSize: '11px', 
              fontWeight: '500',
              color: theme.textMuted,
              letterSpacing: '0.02em'
            }}>
              Filters
            </label>
            <button
              onClick={addFilter}
              style={{
                padding: '4px 10px',
                background: theme.card,
                border: `1px solid ${theme.border}`,
                borderRadius: '5px',
                color: theme.text,
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.cardHover}
              onMouseLeave={(e) => e.currentTarget.style.background = theme.card}
            >
              <Plus size={12} />
              Add
            </button>
          </div>
          
          {config.filters && config.filters.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {config.filters.map((filter, idx) => (
                <div key={idx} style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 0.7fr 1fr auto', 
                  gap: '6px',
                  padding: '6px',
                  background: theme.card,
                  border: `1px solid ${theme.border}`,
                  borderRadius: '6px'
                }}>
                  <select
                    value={filter.field}
                    onChange={(e) => updateFilter(idx, 'field', e.target.value)}
                    style={{
                      padding: '5px',
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                      color: theme.text,
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  >
                    <option value="">Field...</option>
                    {availableFields.map(field => (
                      <option key={field} value={field}>{field}</option>
                    ))}
                  </select>
                  <select
                    value={filter.operator}
                    onChange={(e) => updateFilter(idx, 'operator', e.target.value)}
                    style={{
                      padding: '5px',
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                      color: theme.text,
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  >
                    <option value="=">=</option>
                    <option value="!=">!=</option>
                    <option value=">">{'>'}</option>
                    <option value="<">{'<'}</option>
                    <option value=">=">{'>='}</option>
                    <option value="<=">{'<='}</option>
                    <option value="LIKE">LIKE</option>
                  </select>
                  <input
                    type="text"
                    value={filter.value}
                    onChange={(e) => updateFilter(idx, 'value', e.target.value)}
                    placeholder="Value..."
                    style={{
                      padding: '5px',
                      background: theme.bg,
                      border: `1px solid ${theme.border}`,
                      borderRadius: '4px',
                      color: theme.text,
                      fontSize: '11px',
                      outline: 'none'
                    }}
                  />
                  <button
                    onClick={() => removeFilter(idx)}
                    style={{
                      padding: '5px',
                      background: 'transparent',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '12px', 
              textAlign: 'center', 
              color: theme.textMuted,
              fontSize: '11px',
              background: theme.card,
              border: `1px dashed ${theme.border}`,
              borderRadius: '6px'
            }}>
              No filters applied
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderAxisTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', minHeight: '500px' }}>
      <div>
        {/* X-Axis */}
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderRadius: '8px',
          border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)'
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '10px',
            fontWeight: '700',
            color: theme.textMuted,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            <AlignLeft size={13} />
            X-Axis
          </h3>

          <Input
            label="Axis Label"
            value={config.xAxisLabel}
            onChange={(e) => setConfig({ ...config, xAxisLabel: e.target.value })}
            placeholder="e.g., Time"
            theme={theme}
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Input
              label="Label Size"
              type="number"
              value={config.xAxisLabelFontSize}
              onChange={(e) => setConfig({ ...config, xAxisLabelFontSize: parseInt(e.target.value) || 12 })}
              min="8"
              max="24"
              theme={theme}
            />
            <Select
              label="Label Rotation"
              value={config.xAxisLabelRotation}
              onChange={(e) => setConfig({ ...config, xAxisLabelRotation: parseInt(e.target.value) })}
              theme={theme}
            >
              {LABEL_ROTATIONS.map(rot => (
                <option key={rot.value} value={rot.value}>{rot.label}</option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Input
              label="Tick Size"
              type="number"
              value={config.xAxisTickFontSize}
              onChange={(e) => setConfig({ ...config, xAxisTickFontSize: parseInt(e.target.value) || 11 })}
              min="6"
              max="18"
              theme={theme}
            />
            <Select
              label="Tick Rotation"
              value={config.xAxisTickRotation}
              onChange={(e) => setConfig({ ...config, xAxisTickRotation: parseInt(e.target.value) })}
              theme={theme}
            >
              {LABEL_ROTATIONS.map(rot => (
                <option key={rot.value} value={rot.value}>{rot.label}</option>
              ))}
            </Select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Input
              label="Tick Count"
              value={config.xAxisTickCount}
              onChange={(e) => setConfig({ ...config, xAxisTickCount: e.target.value })}
              placeholder="auto"
              theme={theme}
            />
            <Input
              label="Tick Interval"
              value={config.xAxisTickInterval}
              onChange={(e) => setConfig({ ...config, xAxisTickInterval: e.target.value })}
              placeholder="auto"
              theme={theme}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', fontSize: '11px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.textSecondary, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.xAxisShowLabel}
                onChange={(e) => setConfig({ ...config, xAxisShowLabel: e.target.checked })}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              Show Label
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.textSecondary, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.xAxisShowTicks}
                onChange={(e) => setConfig({ ...config, xAxisShowTicks: e.target.checked })}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              Show Ticks
            </label>
          </div>
        </div>

        {/* Grid */}
        <div style={{
          padding: '12px',
          background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderRadius: '8px',
          border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)'
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '10px',
            fontWeight: '700',
            color: theme.textMuted,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            <Hash size={13} />
            Grid
          </h3>

          <Input
            label={`Stroke Pattern`}
            value={config.gridStrokeDashArray}
            onChange={(e) => setConfig({ ...config, gridStrokeDashArray: e.target.value })}
            placeholder="3 3"
            theme={theme}
          />
          <div style={{ fontSize: '10px', color: theme.textMuted, marginTop: '-8px', marginBottom: '12px' }}>
            Examples: "3 3" (dashed), "1 0" (solid), "0 0" (hidden)
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontSize: '11px', 
              fontWeight: '500',
              color: theme.textMuted
            }}>
              Opacity: {Math.round(config.gridOpacity * 100)}%
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={config.gridOpacity}
              onChange={(e) => setConfig({ ...config, gridOpacity: parseFloat(e.target.value) })}
              style={{ width: '100%', cursor: 'pointer' }}
            />
          </div>
        </div>
      </div>

      {/* Y-Axis */}
      <div>
        <div style={{
          padding: '12px',
          background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          borderRadius: '8px',
          border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)'
        }}>
          <h3 style={{
            margin: '0 0 12px 0',
            fontSize: '10px',
            fontWeight: '700',
            color: theme.textMuted,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase'
          }}>
            <RotateCw size={13} />
            Y-Axis
          </h3>

          <Input
            label="Axis Label"
            value={config.yAxisLabel}
            onChange={(e) => setConfig({ ...config, yAxisLabel: e.target.value })}
            placeholder="e.g., Value, Temperature"
            theme={theme}
          />

          <Select
            label={<><Type size={11} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />Number Format</>}
            value={config.yAxisNumberFormat}
            onChange={(e) => setConfig({ ...config, yAxisNumberFormat: e.target.value })}
            theme={theme}
          >
            {NUMBER_FORMATS.map(format => (
              <option key={format.value} value={format.value}>
                {format.label} - {format.example}
              </option>
            ))}
          </Select>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px', marginBottom: '12px' }}>
            <Input
              label="Decimals"
              type="number"
              value={config.yAxisDecimals}
              onChange={(e) => setConfig({ ...config, yAxisDecimals: parseInt(e.target.value) || 0 })}
              min="0"
              max="10"
              theme={theme}
            />
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: theme.textSecondary, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={config.yAxisUseCommas}
                  onChange={(e) => setConfig({ ...config, yAxisUseCommas: e.target.checked })}
                  style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                />
                Commas
              </label>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Input
              label="Unit"
              value={config.yAxisUnit}
              onChange={(e) => setConfig({ ...config, yAxisUnit: e.target.value })}
              placeholder="e.g., °C, MB"
              theme={theme}
            />
            <Select
              label="Position"
              value={config.yAxisUnitPosition}
              onChange={(e) => setConfig({ ...config, yAxisUnitPosition: e.target.value })}
              theme={theme}
            >
              <option value="prefix">Prefix</option>
              <option value="suffix">Suffix</option>
            </Select>
          </div>

          {config.yAxisNumberFormat === 'custom' && (
            <Input
              label="Custom Format ({value})"
              value={config.yAxisCustomFormat}
              onChange={(e) => setConfig({ ...config, yAxisCustomFormat: e.target.value })}
              placeholder="e.g., {value} units"
              style={{ fontFamily: 'ui-monospace, monospace' }}
              theme={theme}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
            <Input
              label="Label Size"
              type="number"
              value={config.yAxisLabelFontSize}
              onChange={(e) => setConfig({ ...config, yAxisLabelFontSize: parseInt(e.target.value) || 12 })}
              min="8"
              max="24"
              theme={theme}
            />
            <Input
              label="Tick Size"
              type="number"
              value={config.yAxisTickFontSize}
              onChange={(e) => setConfig({ ...config, yAxisTickFontSize: parseInt(e.target.value) || 11 })}
              min="6"
              max="18"
              theme={theme}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <Input
              label="Width"
              value={config.yAxisWidth}
              onChange={(e) => setConfig({ ...config, yAxisWidth: e.target.value })}
              placeholder="auto"
              theme={theme}
            />
            <Select
              label="Side"
              value={config.yAxisPosition}
              onChange={(e) => setConfig({ ...config, yAxisPosition: e.target.value })}
              theme={theme}
            >
              <option value="left">Left</option>
              <option value="right">Right</option>
            </Select>
            <Input
              label="Ticks"
              value={config.yAxisTickCount}
              onChange={(e) => setConfig({ ...config, yAxisTickCount: e.target.value })}
              placeholder="auto"
              theme={theme}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', marginBottom: '12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.textSecondary, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.yAxisShowLabel}
                onChange={(e) => setConfig({ ...config, yAxisShowLabel: e.target.checked })}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              Show Label
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', color: theme.textSecondary, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={config.yAxisShowTicks}
                onChange={(e) => setConfig({ ...config, yAxisShowTicks: e.target.checked })}
                style={{ width: '14px', height: '14px', cursor: 'pointer' }}
              />
              Show Ticks
            </label>
          </div>

          {/* Format Preview */}
          <div style={{ 
            padding: '10px', 
            background: theme.bg, 
            borderRadius: '6px',
            border: `1px dashed ${theme.border}`
          }}>
            <div style={{ fontSize: '10px', color: theme.textMuted, marginBottom: '4px' }}>
              Format Preview:
            </div>
            <div style={{ fontSize: '13px', color: theme.text, fontWeight: '600', fontFamily: 'ui-monospace, monospace' }}>
              {formatTickValue(1234.567)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const sectionBox = {
    background: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    border: darkMode ? '1px solid rgba(255,255,255,0.07)' : '1px solid rgba(0,0,0,0.07)',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 12
  };
  const sectionLabel = {
    display: 'flex', alignItems: 'center', gap: 6,
    marginBottom: 12, fontSize: 10, fontWeight: 700,
    color: theme.textMuted, letterSpacing: '0.08em', textTransform: 'uppercase'
  };

  const renderAdvancedTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', minHeight: '500px' }}>
      {/* LEFT */}
      <div>
        {/* Y-Axis Scaling */}
        <div style={sectionBox}>
          <label style={sectionLabel}>
            <AlignLeft size={12} /> Y-Axis Scaling
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[
              { id: 'auto', label: 'Auto' },
              { id: 'linear', label: 'Linear' },
              { id: 'log', label: 'Top' },
              { id: 'custom', label: 'Custom' }
            ].map(({ id, label }) => {
              const isActive = config.yAxisScale === id;
              return (
                <button
                  key={id}
                  onClick={() => setConfig({ ...config, yAxisScale: id })}
                  style={{
                    flex: 1, padding: '8px 0',
                    background: isActive ? theme.accent : 'transparent',
                    border: `1px solid ${isActive ? theme.accent : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
                    borderRadius: 7, color: isActive ? '#fff' : theme.textSecondary,
                    cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 600 : 400,
                    outline: 'none', transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')}
                  onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'transparent')}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {config.yAxisScale === 'custom' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
              <Input label="Min" type="number" value={config.yAxisMin} onChange={(e) => setConfig({ ...config, yAxisMin: e.target.value })} placeholder="Auto" theme={theme} />
              <Input label="Max" type="number" value={config.yAxisMax} onChange={(e) => setConfig({ ...config, yAxisMax: e.target.value })} placeholder="Auto" theme={theme} />
            </div>
          )}
        </div>

        {/* Panel Size */}
        <div style={sectionBox}>
          <label style={sectionLabel}>
            <Hash size={12} /> Panel Size
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="Width (1-12)" type="number" value={config.width} onChange={(e) => setConfig({ ...config, width: Math.max(1, Math.min(12, parseInt(e.target.value) || 1)) })} min="1" max="12" theme={theme} />
            <Input label="Height (1-10)" type="number" value={config.height} onChange={(e) => setConfig({ ...config, height: Math.max(1, Math.min(10, parseInt(e.target.value) || 1)) })} min="1" max="10" theme={theme} />
          </div>
        </div>

        {/* Data Limit & Refresh */}
        <div style={sectionBox}>
          <label style={sectionLabel}>
            <Database size={12} /> Data Settings
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Input label="Data Limit" type="number" value={config.limit ?? 100} onChange={(e) => { const v = parseInt(e.target.value); setConfig({ ...config, limit: isNaN(v) ? 100 : v }); }} min="10" max="10000" theme={theme} />
            <Select label="Auto Refresh" value={config.refreshInterval} onChange={(e) => setConfig({ ...config, refreshInterval: parseInt(e.target.value) })} theme={theme}>
              <option value={0}>None</option>
              <option value={1000}>1s</option>
              <option value={5000}>5s</option>
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
              <option value={60000}>1m</option>
            </Select>
          </div>
        </div>

        {/* Drill-through — navigate to another dashboard with the active
            cross-filters injected as URL params. Renders as an arrow-up-right
            button in the panel header when a target is set. */}
        <div style={sectionBox}>
          <label style={sectionLabel}>
            <Database size={12} /> Drill-through
          </label>
          <Select
            label="Target dashboard"
            value={config.drillThroughTargetId || ''}
            onChange={(e) => setConfig({ ...config, drillThroughTargetId: e.target.value })}
            theme={theme}
          >
            <option value="">— none (no drill button) —</option>
            {(dashboards || [])
              .filter(d => (d._id || d.id) !== currentDashboardId)
              .map(d => {
                const id = d._id || d.id;
                return <option key={id} value={id}>{d.name}</option>;
              })}
          </Select>
          {config.drillThroughTargetId && (
            <Input
              label="Button label (optional)"
              value={config.drillThroughLabel || ''}
              onChange={(e) => setConfig({ ...config, drillThroughLabel: e.target.value })}
              placeholder="e.g. Investigate"
              theme={theme}
            />
          )}
        </div>
      </div>

      {/* RIGHT — Transformations */}
      <div>
        <TransformationPanel
          transformations={config.transformations || []}
          onChange={(transforms) => setConfig({ ...config, transformations: transforms })}
          darkMode={darkMode}
        />
      </div>
    </div>
  );

  const renderStyleTab = () => {
    const ToggleSwitch = ({ checked, onChange }) => (
      <div
        onClick={() => onChange(!checked)}
        style={{
          width: 40, height: 22, borderRadius: 11,
          background: checked ? theme.accent : theme.card,
          border: `1px solid ${checked ? theme.accent : theme.border}`,
          position: 'relative', cursor: 'pointer',
          transition: 'all 0.2s ease', flexShrink: 0
        }}
      >
        <div style={{
          width: 16, height: 16, borderRadius: '50%',
          background: '#fff',
          position: 'absolute',
          top: 2,
          left: checked ? 20 : 2,
          transition: 'left 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
        }} />
      </div>
    );

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', minHeight: '500px' }}>
        <div>
          {/* Chart Colors — circles with native color picker */}
          <div style={{ ...sectionBox }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <label style={{ ...sectionLabel, marginBottom: 0 }}>
                Chart Colors
              </label>
              <button
                onClick={() => {
                  const currentColors = config.colors || COLORS.slice(0, Math.max(5, (config.yAxes?.length || 1)));
                  const nextIdx = currentColors.length;
                  setConfig({ ...config, colors: [...currentColors, COLORS[nextIdx % COLORS.length]] });
                }}
                style={{
                  fontSize: 10, padding: '2px 8px', background: 'transparent',
                  border: `1px solid ${theme.border}`, borderRadius: 4,
                  color: theme.textMuted, cursor: 'pointer', outline: 'none'
                }}
              >
                + Add
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              {(() => {
                const yCount = Math.max(config.yAxes?.length || 0, config.colors?.length || 0, 5);
                const colorList = Array.from({ length: yCount }, (_, i) => config.colors?.[i] || COLORS[i % COLORS.length]);
                return colorList.map((color, colorIdx) => {
                  const hovered = hoveredColor === colorIdx;
                  const yField = config.yAxes?.[colorIdx] || `Y${colorIdx + 1}`;
                  const label = typeof yField === 'string' ? (yField.length > 8 ? yField.slice(0, 7) + '…' : yField) : `Y${colorIdx + 1}`;
                  return (
                    <div key={colorIdx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                      <div
                        style={{ position: 'relative', width: 40, height: 40, cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredColor(colorIdx)}
                        onMouseLeave={() => setHoveredColor(null)}
                        onClick={() => document.getElementById(`color-picker-${colorIdx}`).click()}
                      >
                        <div style={{
                          width: 40, height: 40, borderRadius: '50%',
                          background: color,
                          border: hovered ? '3px solid rgba(255,255,255,0.5)' : 'none',
                          boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.3)',
                          transform: hovered ? 'scale(1.08)' : 'scale(1)',
                          transition: 'all 0.15s ease'
                        }} />
                        {hovered && (
                          <div style={{
                            position: 'absolute', inset: 0, borderRadius: '50%',
                            background: 'rgba(0,0,0,0.45)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transform: 'scale(1.08)'
                          }}>
                            <Edit2 size={13} color="#fff" />
                          </div>
                        )}
                        <input
                          id={`color-picker-${colorIdx}`}
                          type="color"
                          value={color}
                          onChange={(e) => {
                            const newColors = Array.from({ length: yCount }, (_, i) => config.colors?.[i] || COLORS[i % COLORS.length]);
                            newColors[colorIdx] = e.target.value;
                            setConfig({ ...config, colors: newColors });
                          }}
                          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                        />
                      </div>
                      <span style={{ fontSize: '10px', color: theme.textMuted, fontWeight: '500', maxWidth: 40, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          {/* Legend Position */}
          <div style={{ ...sectionBox }}>
            <label style={sectionLabel}>Legend Position</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {LEGEND_POSITIONS.map(pos => {
                const isActive = config.legendPosition === pos.value;
                return (
                  <button
                    key={pos.value}
                    onClick={() => setConfig({ ...config, legendPosition: pos.value })}
                    style={{
                      flex: 1, padding: '8px 0',
                      background: isActive ? theme.accent : 'transparent',
                      border: `1px solid ${isActive ? theme.accent : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
                      borderRadius: '6px',
                      color: isActive ? '#fff' : theme.textSecondary,
                      cursor: 'pointer', fontSize: '12px', fontWeight: isActive ? 600 : 400,
                      outline: 'none', transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => !isActive && (e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)')}
                    onMouseLeave={(e) => !isActive && (e.currentTarget.style.background = 'transparent')}
                  >
                    {pos.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Line Width + Fill Opacity */}
          <div style={{ ...sectionBox }}>
            <label style={sectionLabel}>Line Width: {config.lineWidth}px</label>
            <input
              type="range" min="1" max="5"
              value={config.lineWidth}
              onChange={(e) => setConfig({ ...config, lineWidth: parseInt(e.target.value) })}
              style={{ width: '100%', cursor: 'pointer', accentColor: theme.accent }}
            />
            {config.vizType === 'area' && (
              <>
                <label style={{ ...sectionLabel, marginTop: 14 }}>Fill Opacity: {Math.round(config.fillOpacity * 100)}%</label>
                <input
                  type="range" min="0" max="1" step="0.1"
                  value={config.fillOpacity}
                  onChange={(e) => setConfig({ ...config, fillOpacity: parseFloat(e.target.value) })}
                  style={{ width: '100%', cursor: 'pointer', accentColor: theme.accent }}
                />
              </>
            )}
          </div>

          {/* Conditional formatting for tables — per-column rules that colour
              cell backgrounds, draw a data bar, or change the text colour. */}
          {config.vizType === 'table' && (
            <div style={{ ...sectionBox }}>
              <label style={sectionLabel}>Conditional Formatting</label>
              {(config.columnFormats || []).map((rule, i) => (
                <div key={i} style={{
                  padding: 8, marginBottom: 6, borderRadius: 6,
                  border: `1px solid ${theme.border}`, background: theme.card
                }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <input
                      value={rule.column || ''}
                      onChange={(e) => {
                        const next = [...config.columnFormats];
                        next[i] = { ...next[i], column: e.target.value };
                        setConfig({ ...config, columnFormats: next });
                      }}
                      placeholder="column name"
                      style={{
                        flex: 1, minWidth: 0, padding: '5px 8px', fontSize: 12,
                        background: theme.bg, color: theme.text,
                        border: `1px solid ${theme.border}`, borderRadius: 4
                      }}
                    />
                    <button onClick={() => {
                      const next = config.columnFormats.filter((_, j) => j !== i);
                      setConfig({ ...config, columnFormats: next });
                    }} style={{
                      background: 'transparent', border: 'none',
                      color: '#f87171', cursor: 'pointer', padding: '3px 6px', fontSize: 14
                    }}>×</button>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                    <select
                      value={rule.kind || 'colorScale'}
                      onChange={(e) => {
                        const next = [...config.columnFormats];
                        next[i] = { ...next[i], kind: e.target.value };
                        setConfig({ ...config, columnFormats: next });
                      }}
                      style={{
                        flex: 1, padding: '5px 8px', fontSize: 12,
                        background: theme.bg, color: theme.text,
                        border: `1px solid ${theme.border}`, borderRadius: 4
                      }}
                    >
                      <option value="colorScale">Color scale (min → max)</option>
                      <option value="dataBars">Data bars</option>
                      <option value="textColor">Text color by threshold</option>
                    </select>
                  </div>
                  {(rule.kind === 'colorScale' || rule.kind === 'dataBars') && (
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <label style={{ fontSize: 10, color: theme.textMuted, flex: 1 }}>
                        {rule.kind === 'colorScale' ? 'low' : 'bar off'}
                        <input
                          type="color"
                          value={rule.colorLow || '#0f172a'}
                          onChange={(e) => {
                            const next = [...config.columnFormats];
                            next[i] = { ...next[i], colorLow: e.target.value };
                            setConfig({ ...config, columnFormats: next });
                          }}
                          style={{ display: 'block', width: '100%', height: 24, border: 'none', background: 'transparent', cursor: 'pointer' }}
                        />
                      </label>
                      <label style={{ fontSize: 10, color: theme.textMuted, flex: 1 }}>
                        {rule.kind === 'colorScale' ? 'high' : 'bar color'}
                        <input
                          type="color"
                          value={rule.colorHigh || '#7c3aed'}
                          onChange={(e) => {
                            const next = [...config.columnFormats];
                            next[i] = { ...next[i], colorHigh: e.target.value };
                            setConfig({ ...config, columnFormats: next });
                          }}
                          style={{ display: 'block', width: '100%', height: 24, border: 'none', background: 'transparent', cursor: 'pointer' }}
                        />
                      </label>
                    </div>
                  )}
                  {rule.kind === 'textColor' && (
                    <>
                      <input
                        type="number"
                        value={rule.threshold ?? ''}
                        onChange={(e) => {
                          const next = [...config.columnFormats];
                          next[i] = { ...next[i], threshold: e.target.value };
                          setConfig({ ...config, columnFormats: next });
                        }}
                        placeholder="threshold value"
                        style={{
                          width: '100%', padding: '5px 8px', fontSize: 12, marginBottom: 6,
                          background: theme.bg, color: theme.text,
                          border: `1px solid ${theme.border}`, borderRadius: 4
                        }}
                      />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <label style={{ fontSize: 10, color: theme.textMuted, flex: 1 }}>
                          ≥ threshold
                          <input type="color" value={rule.above || '#10b981'}
                            onChange={(e) => {
                              const next = [...config.columnFormats];
                              next[i] = { ...next[i], above: e.target.value };
                              setConfig({ ...config, columnFormats: next });
                            }}
                            style={{ display: 'block', width: '100%', height: 24, border: 'none', background: 'transparent', cursor: 'pointer' }}
                          />
                        </label>
                        <label style={{ fontSize: 10, color: theme.textMuted, flex: 1 }}>
                          &lt; threshold
                          <input type="color" value={rule.below || '#ef4444'}
                            onChange={(e) => {
                              const next = [...config.columnFormats];
                              next[i] = { ...next[i], below: e.target.value };
                              setConfig({ ...config, columnFormats: next });
                            }}
                            style={{ display: 'block', width: '100%', height: 24, border: 'none', background: 'transparent', cursor: 'pointer' }}
                          />
                        </label>
                      </div>
                    </>
                  )}
                </div>
              ))}
              <button onClick={() => {
                const next = [...(config.columnFormats || []),
                  { column: '', kind: 'colorScale', colorLow: '#0f172a', colorHigh: '#7c3aed' }];
                setConfig({ ...config, columnFormats: next });
              }} style={{
                width: '100%', padding: '6px 10px', fontSize: 12, fontWeight: 500,
                background: 'transparent', border: `1px dashed ${theme.border}`,
                color: theme.accent, borderRadius: 4, cursor: 'pointer'
              }}>+ Add rule</button>
            </div>
          )}

          {/* Calendar heatmap controls */}
          {config.vizType === 'calendarHeatmap' && (
            <div style={{ ...sectionBox }}>
              <label style={sectionLabel}>Calendar Heatmap</label>
              <Select
                label="Daily aggregation"
                value={config.heatmapAggregation || 'sum'}
                onChange={(e) => setConfig({ ...config, heatmapAggregation: e.target.value })}
                theme={theme}
              >
                <option value="sum">Sum of values that day</option>
                <option value="avg">Average of values that day</option>
                <option value="max">Max value that day</option>
              </Select>
            </div>
          )}

          {/* KPI card controls — visible only for vizType === 'stat' */}
          {config.vizType === 'stat' && (
            <div style={{ ...sectionBox }}>
              <label style={sectionLabel}>KPI Card</label>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: theme.textSecondary }}>Show comparison (vs. start)</span>
                <ToggleSwitch
                  checked={config.kpiShowComparison !== false}
                  onChange={(val) => setConfig({ ...config, kpiShowComparison: val })}
                />
              </div>
              {config.kpiShowComparison !== false && (
                <>
                  <Select
                    label="Compare current to"
                    value={config.kpiCompareTo || 'first'}
                    onChange={(e) => setConfig({ ...config, kpiCompareTo: e.target.value })}
                    theme={theme}
                  >
                    <option value="first">First value in window (vs. start)</option>
                    <option value="previous">Previous data point (vs. prev)</option>
                    <option value="average">Window average (vs. avg)</option>
                  </Select>
                  <Select
                    label="Good direction"
                    value={config.kpiGoodDirection || 'up'}
                    onChange={(e) => setConfig({ ...config, kpiGoodDirection: e.target.value })}
                    theme={theme}
                  >
                    <option value="up">Higher is better (green when up)</option>
                    <option value="down">Lower is better (green when down)</option>
                    <option value="none">Neutral (no colour signal)</option>
                  </Select>
                </>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: theme.textSecondary }}>Show sparkline</span>
                  <ToggleSwitch
                    checked={config.kpiShowSparkline !== false}
                    onChange={(val) => setConfig({ ...config, kpiShowSparkline: val })}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 13, color: theme.textSecondary }}>Show min / max / avg</span>
                  <ToggleSwitch
                    checked={config.kpiShowStats !== false}
                    onChange={(val) => setConfig({ ...config, kpiShowStats: val })}
                  />
                </div>
              </div>

              {/* Visual alert thresholds */}
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
                <label style={{
                  ...sectionLabel, marginBottom: 8,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span>Visual alerts</span>
                  <span style={{ fontSize: 10, color: theme.textMuted, fontWeight: 400 }}>
                    First matching rule wins
                  </span>
                </label>
                {(config.visualAlerts || []).map((rule, i) => (
                  <div key={i} style={{
                    padding: 8, marginBottom: 6, borderRadius: 6,
                    background: theme.card, border: `1px solid ${theme.border}`
                  }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 11, color: theme.textMuted }}>if value</span>
                      <select
                        value={rule.op || '>='}
                        onChange={(e) => {
                          const next = [...config.visualAlerts];
                          next[i] = { ...next[i], op: e.target.value };
                          setConfig({ ...config, visualAlerts: next });
                        }}
                        style={{
                          padding: '4px 6px', fontSize: 12,
                          background: theme.bg, color: theme.text,
                          border: `1px solid ${theme.border}`, borderRadius: 4
                        }}
                      >
                        {['>=', '<=', '>', '<', '=', '!='].map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <input
                        type="number"
                        value={rule.value ?? ''}
                        onChange={(e) => {
                          const next = [...config.visualAlerts];
                          next[i] = { ...next[i], value: e.target.value };
                          setConfig({ ...config, visualAlerts: next });
                        }}
                        placeholder="threshold"
                        style={{
                          flex: 1, minWidth: 0, padding: '4px 8px', fontSize: 12,
                          background: theme.bg, color: theme.text,
                          border: `1px solid ${theme.border}`, borderRadius: 4
                        }}
                      />
                      <button onClick={() => {
                        const next = config.visualAlerts.filter((_, j) => j !== i);
                        setConfig({ ...config, visualAlerts: next });
                      }} style={{
                        background: 'transparent', border: 'none',
                        color: '#f87171', cursor: 'pointer',
                        padding: '3px 6px', fontSize: 14
                      }}>×</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        value={rule.label || ''}
                        onChange={(e) => {
                          const next = [...config.visualAlerts];
                          next[i] = { ...next[i], label: e.target.value };
                          setConfig({ ...config, visualAlerts: next });
                        }}
                        placeholder="badge label (e.g. Critical)"
                        style={{
                          flex: 1, minWidth: 0, padding: '4px 8px', fontSize: 12,
                          background: theme.bg, color: theme.text,
                          border: `1px solid ${theme.border}`, borderRadius: 4
                        }}
                      />
                      <input
                        type="color"
                        value={rule.color || '#ef4444'}
                        onChange={(e) => {
                          const next = [...config.visualAlerts];
                          next[i] = { ...next[i], color: e.target.value };
                          setConfig({ ...config, visualAlerts: next });
                        }}
                        style={{ width: 32, height: 24, border: 'none', background: 'transparent', cursor: 'pointer' }}
                      />
                    </div>
                  </div>
                ))}
                <button onClick={() => {
                  const next = [...(config.visualAlerts || []),
                    { op: '>=', value: '', color: '#ef4444', label: 'Alert' }];
                  setConfig({ ...config, visualAlerts: next });
                }} style={{
                  width: '100%', padding: '6px 10px', fontSize: 12, fontWeight: 500,
                  background: 'transparent', border: `1px dashed ${theme.border}`,
                  color: theme.accent, borderRadius: 4, cursor: 'pointer'
                }}>+ Add alert rule</button>
              </div>
            </div>
          )}

          {/* Toggle switches */}
          <div style={{ ...sectionBox, marginBottom: 0 }}>
            <label style={sectionLabel}>Display</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
                { label: 'Legends', key: 'showLegend' },
                { label: 'Grid Line', key: 'showGrid' },
                { label: 'Datapoint', key: 'showDots' }
              ].map(({ label, key }) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '13px', color: theme.textSecondary }}>{label}</span>
                  <ToggleSwitch
                    checked={!!config[key]}
                    onChange={(val) => setConfig({ ...config, [key]: val })}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '11px', fontWeight: '600', color: theme.textMuted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Preview
          </label>
          <div style={{ background: theme.card, border: `1px solid ${theme.border}`, borderRadius: '8px', padding: '12px', minHeight: '300px' }}>
            {renderPreviewChart()}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        background: theme.bg,
        borderRadius: '12px',
        width: '100%',
        maxWidth: '1100px',
        height: '700px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadowLg,
        border: `1px solid ${theme.border}`,
        overflow: 'hidden'
      }}>
        {/* Header - Row 1: title + actions */}
        <div style={{
          padding: '12px 20px',
          background: theme.card,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16
        }}>
          {/* Left: icon + title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 34, height: 34,
              background: 'rgba(99,102,241,0.15)',
              border: '1px solid rgba(99,102,241,0.25)',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0
            }}>
              <Tag size={16} color="#a78bfa" />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: theme.text, lineHeight: 1.2 }}>
                {panel ? 'Edit Panel' : 'Add New Panel'}
              </div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 1 }}>
                Configure your visualization
              </div>
            </div>
          </div>

          {/* Right: Cancel + Save */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={onClose}
              style={{
                padding: '7px 16px',
                background: 'transparent',
                border: `1px solid ${theme.border}`,
                borderRadius: 7,
                color: theme.textMuted,
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                outline: 'none',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.cardHover}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              Cancel
            </button>
            <button
              onClick={() => { onSave(config); }}
              style={{
                padding: '7px 18px',
                background: theme.accent,
                border: 'none',
                outline: 'none',
                borderRadius: 7,
                color: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.accentHover}
              onMouseLeave={(e) => e.currentTarget.style.background = theme.accent}
            >
              <Save size={14} />
              {panel ? 'Save' : 'Add'}
            </button>
          </div>
        </div>

        {/* Header - Row 2: tabs full width equally spaced */}
        <div style={{
          borderBottom: `1px solid ${theme.border}`,
          background: theme.card,
          display: 'flex',
          padding: '8px 16px',
          gap: 6
        }}>
          {[
            { id: 'data', label: 'Data', icon: Database },
            { id: 'axes', label: 'Axes', icon: AlignLeft },
            { id: 'style', label: 'Style', icon: Eye },
            { id: 'advanced', label: 'Advanced', icon: Settings }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  background: isActive ? theme.accent : 'transparent',
                  border: 'none',
                  outline: 'none',
                  borderRadius: 8,
                  color: isActive ? '#fff' : theme.textMuted,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => !isActive && (e.currentTarget.style.color = theme.text)}
                onMouseLeave={(e) => !isActive && (e.currentTarget.style.color = theme.textMuted)}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Content - takes all remaining space */}
        <div style={{ 
          flex: 1,
          overflow: 'auto', 
          padding: '20px',
          background: theme.bg
        }}>
          {activeTab === 'data' && renderDataTab()}
          {activeTab === 'axes' && renderAxisTab()}
          {activeTab === 'style' && renderStyleTab()}
          {activeTab === 'advanced' && renderAdvancedTab()}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        input[type="datetime-local"]::-webkit-calendar-picker-indicator {
          filter: ${darkMode ? 'invert(1) brightness(0.7)' : 'none'};
          cursor: pointer;
          opacity: 0.7;
        }
        input[type="datetime-local"]::-webkit-calendar-picker-indicator:hover {
          opacity: 1;
        }
        
        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          background: ${theme.border};
          border-radius: 2px;
          outline: none;
        }
        
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          background: ${theme.accent};
          border-radius: 50%;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        
        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.1);
        }
        
        input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;
          background: ${theme.accent};
          border-radius: 50%;
          cursor: 'pointer';
          border: none;
          transition: all 0.15s ease;
        }
        
        input[type="range"]::-moz-range-thumb:hover {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
}

export default PanelConfigModal;